import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { usePageMeta } from '../lib/usePageMeta';
import { trackConversion, trackBioLink } from '../lib/analytics';

/**
 * PÁGINA DE LINKS (/links) — o destino único do "link da bio".
 *
 * Por que existe: a semana de conteúdo é toda de feed, sem story. Story é o
 * único lugar do Instagram, fora da bio, onde o link é clicável — sem ele, TODO
 * o tráfego social passa por um link só. Um link não leva a lugares diferentes;
 * esta página leva.
 *
 * Como usar: a bio aponta sempre para cá e só o `utm_content` muda a cada post.
 * O `attribution.ts` captura a UTM na chegada e guarda na sessão, então ela
 * sobrevive à navegação interna e chega no evento de conversão — nada precisa
 * ser propagado à mão.
 *
 *   /links?utm_source=instagram-organico&utm_medium=social
 *          &utm_campaign=organico-usucapiao&utm_content=seg-contrato-de-gaveta
 *
 * `noindex` e fora do sitemap: é utilitário de tráfego. Indexar só criaria
 * concorrência com as páginas que devem rankear.
 *
 * Estrutura (cabeçalho em cartão, ícone à esquerda, seta à direita, duas
 * colunas no desktop) veio de uma referência de link-in-bio. Paleta, tipografia
 * e tom são os do Pacces — referência dá composição, não identidade.
 */

const WHATSAPP = '5511969241378';
const TELEFONE = '+551123690371';
const EMAIL = 'contato@paccesadvogados.com.br';
const MAPS =
    'https://www.google.com/maps/search/?api=1&query=Av.+Ordem+e+Progresso,+157,+Sala+408,+Barra+Funda,+S%C3%A3o+Paulo';

const INSTAGRAM = 'https://www.instagram.com/paccesadvogados/';

const Ico = ({ children }: { children: ReactNode }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-[18px] w-[18px]"
        aria-hidden
    >
        {children}
    </svg>
);

const icones = {
    whatsapp: <Ico><path d="M3 21l1.7-5A8.5 8.5 0 1 1 8 19.3z" /><path d="M8.6 9.4c.4 2.6 3.4 5.6 6 6l1.2-1.7 2 1-.6 1.6c-2.9.8-7.9-3.2-9.2-6.3l1.5-.9z" /></Ico>,
    casa: <Ico><path d="M3 10.5 12 4l9 6.5" /><path d="M5.5 9.6V20h13V9.6" /><path d="M10 20v-5.5h4V20" /></Ico>,
    chave: <Ico><circle cx="8" cy="8" r="4" /><path d="M11 11l9 9" /><path d="M17 17l2-2" /></Ico>,
    livro: <Ico><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5z" /><path d="M8 7.5h8M8 11h6" /></Ico>,
    pasta: <Ico><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" /></Ico>,
    fone: <Ico><path d="M5 4h4l1.5 4-2.2 1.4a12 12 0 0 0 6.3 6.3L16 13.5 20 15v4a1 1 0 0 1-1.1 1A16 16 0 0 1 4 5.1 1 1 0 0 1 5 4z" /></Ico>,
    carta: <Ico><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3.5 6.5 8.5 6 8.5-6" /></Ico>,
    pino: <Ico><path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" /></Ico>,
    instagram: <Ico><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.2" cy="6.8" r="0.9" /></Ico>,
    site: <Ico><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.6 2.5 15.4 0 18M12 3c-2.5 2.6-2.5 15.4 0 18" /></Ico>,
};

interface Destino {
    /** Identificador estável enviado no evento — não muda se o rótulo mudar. */
    id: string;
    rotulo: string;
    apoio: string;
    icone: ReactNode;
    href?: string;
    to?: string;
    aoClicar?: () => void;
}

export const LinksPage = () => {
    usePageMeta({
        title: 'Pacces Advogados · Links',
        description:
            'Fale com o escritório, conheça as áreas de atuação e leia o conteúdo sobre direito imobiliário.',
        robots: 'noindex, nofollow',
    });

    const whatsappHref = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
        'Olá! Vim pelo Instagram e gostaria de falar com o escritório.',
    )}`;

    const secundarios: Destino[] = [
        { id: 'usucapiao', rotulo: 'Regularizar um imóvel', apoio: 'Usucapião', icone: icones.casa, to: '/lp/usucapiao' },
        { id: 'compra-e-venda', rotulo: 'Comprar ou vender', apoio: 'Assessoria na negociação', icone: icones.chave, to: '/lp/compra-e-venda' },
        { id: 'conteudo', rotulo: 'Conteúdo', apoio: 'Artigos e guias', icone: icones.livro, to: '/conteudo' },
        { id: 'areas', rotulo: 'Áreas de atuação', apoio: 'O que o escritório faz', icone: icones.pasta, to: '/areas/imobiliario' },
        { id: 'telefone', rotulo: 'Telefone', apoio: '(11) 2369-0371', icone: icones.fone, href: `tel:${TELEFONE}`, aoClicar: () => trackConversion('sp', 'phone') },
        { id: 'email', rotulo: 'E-mail', apoio: 'contato@paccesadvogados.com.br', icone: icones.carta, href: `mailto:${EMAIL}`, aoClicar: () => trackConversion('sp', 'email') },
        { id: 'mapa', rotulo: 'Endereço', apoio: 'Av. Ordem e Progresso, 157 — Barra Funda, SP', icone: icones.pino, href: MAPS },
        { id: 'site', rotulo: 'Site', apoio: 'paccesadvogados.com.br', icone: icones.site, href: 'https://paccesadvogados.com.br' },
    ];

    const externo = (href?: string) => !!href?.startsWith('http');

    const Linha = ({ d, destaque = false }: { d: Destino; destaque?: boolean }) => {
        const base = destaque
            ? 'bg-vanguard-primary text-pacces-ebony hover:bg-[#d3b291]'
            : 'bg-white/[0.035] border border-white/10 text-white hover:border-vanguard-primary/60 hover:bg-white/[0.06]';

        const conteudo = (
            <>
                <span
                    className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${
                        destaque ? 'bg-pacces-ebony/10 text-pacces-ebony' : 'bg-white/[0.06] text-vanguard-primary'
                    }`}
                >
                    {d.icone}
                </span>

                <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-semibold tracking-[0.06em] uppercase">
                        {d.rotulo}
                    </span>
                    {/* Sem `truncate`: em duas colunas o e-mail e o endereço
                        eram cortados no meio. Duas linhas cabem. */}
                    <span
                        className={`mt-0.5 block text-[12px] leading-snug ${
                            destaque ? 'text-pacces-ebony/65' : 'text-stone-400'
                        }`}
                    >
                        {d.apoio}
                    </span>
                </span>

                <span
                    aria-hidden
                    className={`mt-0.5 shrink-0 text-sm transition-transform group-hover:translate-x-0.5 ${
                        destaque ? 'text-pacces-ebony/60' : 'text-stone-500'
                    }`}
                >
                    {externo(d.href) ? '↗' : '→'}
                </span>
            </>
        );

        // Todo destino registra o clique. Os de contato disparam TAMBÉM o
        // conversion_click, que é o evento já importado como conversão.
        const aoClicar = () => {
            trackBioLink(d.id);
            d.aoClicar?.();
        };

        const classes = `group flex items-start gap-4 rounded-xl px-4 py-4 transition-colors duration-200 ${base}`;

        return d.to ? (
            <Link to={d.to} onClick={aoClicar} className={classes}>{conteudo}</Link>
        ) : (
            <a
                href={d.href}
                target={externo(d.href) ? '_blank' : undefined}
                rel={externo(d.href) ? 'noopener noreferrer' : undefined}
                onClick={aoClicar}
                className={classes}
            >
                {conteudo}
            </a>
        );
    };

    return (
        // Sem PageTransition: é a primeira tela de quem vem do Instagram e não
        // pode depender de animação para aparecer.
        <div className="min-h-screen bg-pacces-ebony text-white font-vanguard-sans">
            <div className="mx-auto w-full max-w-2xl px-5 py-12 sm:py-16">
                {/* Cabeçalho em cartão */}
                {/* `[&_p]:text-center`: o index.css justifica todo <p> em @layer base, e
                    isso vence a herança do text-center deste cabeçalho. */}
                <header className="rounded-2xl border border-white/10 bg-white/[0.035] px-6 py-9 text-center [&_p]:text-center">
                    <div className="mx-auto grid h-20 w-20 place-items-center rounded-2xl border border-vanguard-primary/25 bg-black/60">
                        <img
                            src="/logo/Logo-Pacces-Advogados-Branco.png"
                            alt="Pacces Advogados"
                            className="w-[4.25rem]"
                        />
                    </div>

                    <h1 className="mt-6 font-serif text-[28px] leading-none">Pacces Advogados</h1>
                    {/* `text-indent` compensa o letter-spacing: o tracking é aplicado também
        DEPOIS da última letra, o que empurra o centro óptico para a esquerda
        em texto centralizado. O recuo devolve o bloco ao eixo. */}
                    <p className="mt-2 text-[11px] tracking-[0.3em] [text-indent:0.3em] uppercase text-vanguard-primary">
                        Direito Imobiliário · São Paulo
                    </p>
                    <p className="mx-auto mt-4 max-w-sm font-vanguard-blog text-[15px] leading-relaxed text-stone-400">
                        Regularização de imóveis, usucapião e assessoria em compra e venda.
                    </p>

                    <div className="mt-6 flex items-center justify-center gap-3">
                        <a
                            href={INSTAGRAM}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram do escritório"
                            className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-stone-300 transition-colors hover:border-vanguard-primary/60 hover:text-vanguard-primary"
                        >
                            {icones.instagram}
                        </a>
                        <a
                            href="https://paccesadvogados.com.br"
                            aria-label="Site do escritório"
                            className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-stone-300 transition-colors hover:border-vanguard-primary/60 hover:text-vanguard-primary"
                        >
                            {icones.site}
                        </a>
                    </div>
                </header>

                {/* Ação principal em largura cheia; o resto em duas colunas no desktop */}
                <div className="mt-4">
                    <Linha
                        destaque
                        d={{
                            id: 'whatsapp',
                            rotulo: 'Falar no WhatsApp',
                            apoio: 'Atendimento em horário comercial',
                            icone: icones.whatsapp,
                            href: whatsappHref,
                            aoClicar: () => trackConversion('sp', 'whatsapp'),
                        }}
                    />
                </div>

                <nav className="mt-3 grid gap-3 sm:grid-cols-2">
                    {secundarios.map((d) => (
                        <Linha key={d.rotulo} d={d} />
                    ))}
                </nav>

                {/* Crédito ApexDev: centralizado, em linha própria — mesmo
                    tratamento do rodapé do site. */}
                <footer className="mt-10 flex justify-center">
                    <a
                        href="https://devapex.us"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 opacity-40 transition-opacity hover:opacity-70"
                    >
                        <span className="text-[12px] font-medium uppercase tracking-[0.2em] text-stone-500">
                            Developed by
                        </span>
                        <img src="/apexdev_linear_white.svg" alt="ApexDev" className="h-5 w-auto" />
                    </a>
                </footer>
            </div>
        </div>
    );
};
